import { useCallback, useEffect, useMemo, useState } from 'react';

import { PhysicianBioHero } from '@/design-system/molecules/PhysicianBioHero';
import { useGenericLists } from '@/hooks/useGenericLists';
import { arrayMatchValues } from '@/utils/arrayMatchValues';

const IntegrationPhysicianBioHero = (props) => {
    const provider = props?.provider;
    const { data } = useGenericLists('provider-affiliations-badges');
    const specialtiesArray = provider?.specialties?.split(';');
    const practiceGroups = provider?.practiceGroups?.map((group) => group.name);
    const affiliationBadgeDictionary = useMemo(() => data, [data]);
    const languagesArray = provider?.language?.split(';');
    const hasPhoneNumber = provider?.locations?.find((location) => location.phone?.trim());
    const phoneNumber = hasPhoneNumber ? hasPhoneNumber?.phone : '800-247-9580';
    const additionalVisitTypes = provider?.additionalVisitTypes?.split(';');
    const [badgeLabel, setBadgeLabel] = useState('');

    /**
     * Gets the provider affiliation label based on the
     * practiceGroups and affiliationBadgeDictionary arrays comparison
     *
     * 1. compare practiceGroups and affiliationBadgeDictionary
     * 2. filter the affiliationBadgeDictionary by using the affiliationBadge
     * 3. set the title value from the filtered array
     */
    const getAffiliationLabel = useCallback(() => {
        // 1. compare practiceGroups and affiliationBadgeDictionary thru arrayMatchValues method
        // to return the affiliationBadge array with a single value
        const affiliationBadge =
            practiceGroups?.length && affiliationBadgeDictionary?.length
                ? arrayMatchValues(
                      practiceGroups,
                      affiliationBadgeDictionary?.map((badge) => badge.value),
                  )
                : null;

        // 2. filter the affiliationBadgeDictionary by using the affiliationBadge
        if (affiliationBadge?.length) {
            let affiliationBadgeLabel = null;
            affiliationBadgeLabel = affiliationBadgeDictionary?.filter(
                (badge) => badge.value === affiliationBadge,
            );

            // 3. set the title value from the previous filtering
            if (affiliationBadgeLabel?.length) {
                const [{ title }] = affiliationBadgeLabel;
                setBadgeLabel(title);
            }
        } else {
            setBadgeLabel('');
        }
    }, [affiliationBadgeDictionary, practiceGroups]);

    useEffect(getAffiliationLabel, [getAffiliationLabel]);

    return (
        <PhysicianBioHero
            id={props?.id}
            breadcrumbs={props?.breadcrumbs}
            providerImage={provider?.image}
            acceptingNewPatients={provider?.acceptingNewPatients}
            onlineBooking={provider?.onlineBookingAvailable}
            additionalVisitTypes={additionalVisitTypes}
            providerName={provider?.title?.replace(',', ', ')}
            primarySpecialty={provider?.primarySpecialty}
            specialties={specialtiesArray}
            averageRating={props?.reviews?.overallRating}
            numberOfReviews={props?.reviews?.totalRatingCount}
            affiliationBadgeLabel={badgeLabel}
            languages={languagesArray}
            gender={provider?.gender}
            lgbtServices={provider?.lgbtServices}
            phoneNumber={phoneNumber}
            npi={provider?.npi}
            newPatientAppointmentIds={props?.newPatientAppointmentIds}
            appointmentUrl={props?.appointmentUrl}
            insurancesText={props?.insurancesText}
            insuranceLink={props?.insuranceLink}
            insurancesLinkText={props?.insurancesLinkText}
            bookAppointmentText={props?.bookAppointmentText}
            availabilityText={props?.availabilityText}
            departmentIds={props?.departmentId}
        />
    );
};

export default IntegrationPhysicianBioHero;
